import { Auth0ContextInterface, useAuth0 } from "@auth0/auth0-react";
import type { GetTokenSilentlyOptions } from "@auth0/auth0-spa-js";
import {
  MutationKey,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import type { SerializedObject } from "../../../api/src/lib";
import type { ApiErrorBody } from "../../../api/src/lib/errorBody";
import { processFileResponse } from "./file";

type UseApiOptions = GetTokenSilentlyOptions["authorizationParams"] &
  Omit<RequestInit, "body"> & { body?: any };

/**
 * Error thrown by `publicFetch` for any non-2xx HTTP response. Unlike a bare
 * `Error`, it preserves the HTTP `status` (and the raw response `body`) so
 * callers can distinguish, e.g., a 403 permission denial from a genuine
 * server-side failure rather than collapsing everything into one toast.
 *
 * APP-1327 (s-security-master-permission-error-019effd3): the Security Master save
 * handlers branch on `status === 403` to surface a clear "you need admin
 * permissions" message while leaving the generic message for every other
 * failure.
 */
export class ApiError extends Error {
  /** The HTTP status code of the failed response (e.g. 403). */
  readonly status: number;
  /** The raw response body text, when available. */
  readonly body: string;

  constructor(status: number, body: string) {
    // Keep the historic `"Request failed: <body>"` message format verbatim:
    // existing consumers (HouseholdRMD `extractServerMessage`,
    // RebalancesApprovedTable `parseSubmitError`) slice the body out of this
    // exact prefix. The status is carried as a separate field, not folded in.
    super(`Request failed: ${body}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    // Restore prototype chain for reliable `instanceof` across transpilation.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * What the api answered a failed request with, recovered from the error
 * `publicFetch` threw, or `null` when there is nothing to recover — the error
 * did not come from a response at all (a network failure, a bug in a handler, a
 * non-`Error` value), or the response body was JSON that is not an object.
 *
 * `body` is the api's documented error body — `{ statusCode, message, details? }`,
 * defined once in `api/src/lib/errorBody.ts` and written by the api's single
 * exception filter (APP-1526). `text` is the same body before parsing, and is
 * the only thing left when a response did NOT come from this api: a proxy's
 * plain-text 502, a load balancer's HTML. So `text` is prose for a human when
 * `body` is `null`, never a fragment of someone else's JSON.
 *
 * Every console parser that reads a failure goes through here —
 * `parseSubmitError` (`rebalances/RebalancesApprovedTable.tsx`),
 * `extractServerMessage` (below),
 * `missingSlantDefinitions` and `slantTokenRenewal`
 * (`clients/household/HouseholdCashBilling.tsx`), `contactErrorMessage`
 * (`clients/contact/lib.ts`), `stepErrorMessage`
 * (`clients/household/switch-to-slant/api.ts`) — so the six of them cannot
 * drift apart about how a body is recovered, only about which fields they want
 * from it.
 *
 * The recovery is by string prefix because that is how the body arrives:
 * `ApiError` folds the whole body into its message as `"Request failed: <body>"`
 * (see the constructor above) and nothing types the coupling. Accepting any
 * `Error` with that prefix, not only an `ApiError`, keeps the older
 * `new Error("Request failed: …")` form working.
 *
 * @param err The value caught from a failed request
 */
export function serverError(
  err: unknown,
): { body: ApiErrorBody | null; text: string } | null {
  const prefix = "Request failed: ";
  if (!(err instanceof Error) || !err.message.startsWith(prefix)) {
    return null;
  }
  const text = err.message.slice(prefix.length).trim();
  if (!text) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    // Not JSON, so not this api's error body. The text is still what the
    // response said, and a caller may have nothing better to show.
    return { body: null, text };
  }

  // Parsed, but not an object: `null`, a bare string, a number. Whatever
  // answered speaks JSON and still did not send the documented body, so there
  // is no sentence here and nothing a caller should show. Handing back `text`
  // would put the literal word `null` on screen, or `"boom"` complete with its
  // JSON quotes. `null` is the answer this function already gives for every
  // other "nothing recoverable here" case above.
  //
  // An array is NOT in this group — `typeof [] === "object"` — so it takes the
  // branch below and yields a body with no fields the parsers want.
  if (parsed === null || typeof parsed !== "object") {
    return null;
  }
  return { body: parsed as ApiErrorBody, text };
}

/**
 * Extract a human-readable server error message from an API error.
 *
 * `authenticatedFetch` throws `new Error("Request failed: <body text>")`, so
 * the body reaches the console only inside that message.
 *
 * APP-1526: the api answers EVERY failure with the documented error body
 * (`api/src/lib/errorBody.ts`), whose sentence is always `message` and always a
 * string. That makes this the general reader for the whole api rather than for
 * the Nest exceptions alone. Two things it used to recover nothing from now
 * work: the five refusals on `PATCH /rebalances`, which called their sentence
 * `error` and left this parser to fall through to a generic toast; and the
 * reports 404s, which were plain text.
 *
 * The non-JSON branch is kept. A response that did not come from this api —
 * a proxy's plain-text 502, a load balancer's HTML — never carried the
 * documented body, and its text is still the most useful thing we have.
 *
 * Returns `null` when no specific message is available so callers can fall back
 * to a generic toast.
 *
 * It sits here rather than in one of the console screens that reads it because
 * it wants nothing from any of them: the two cash & billing save handlers
 * (`clients/account/AccountCashBillingSettings.tsx`,
 * `clients/household/HouseholdAccountCashSettings.tsx`) need exactly the
 * sentence `HouseholdRMD.tsx` already needed, and importing a component module
 * for one pure function — or keeping a third copy of it — is the alternative.
 * `parseSubmitError` (`rebalances/RebalancesApprovedTable.tsx`) stays where it
 * is: it wants the rebalance route's `warnings` as well, not the sentence
 * alone.
 */
export function extractServerMessage(err: unknown): string | null {
  const answer = serverError(err);
  if (answer === null) return null;
  if (answer.body === null) return answer.text;
  const message = answer.body.message;
  return typeof message === "string" && message.length > 0 ? message : null;
}

/**
 * The sentence the api refused a request with, or `undefined` when the failure
 * is not a refusal.
 *
 * A refusal is a response with a status from 400 to 499: the api explains why
 * it did not do what was asked, and the sentence is for the person who asked.
 * Every other failure has no sentence to show — a 5xx (the api fills `message`
 * on every error, with "Unexpected error" when it has nothing to say, see
 * `api/src/lib/errorBody.ts`), a response that did not come from this api, a
 * network failure. For those the caller shows its own fixed text.
 *
 * The status comes from the `ApiError`, the body from `serverError`.
 *
 * @param err The value caught from a failed request
 */
export function refusalSentence(err: unknown): string | undefined {
  if (!(err instanceof ApiError) || err.status < 400 || err.status > 499) {
    return undefined;
  }
  const message = serverError(err)?.body?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : undefined;
}

/**
 * Type guard for a 403 Forbidden response — i.e. the request was rejected for
 * lack of permission rather than failing for an unknown reason. Narrowly checks
 * the status so unknown errors (network failures, 4xx other than 403, 5xx) do
 * NOT match and fall through to generic handling.
 * @param error The value caught from a failed request
 */
export function isForbiddenError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 403;
}

/**
 * Callback for processing JSON response bodies.
 * @param res The HTTP response object
 */
export function processJsonResponse(res: Response) {
  return res.json();
}

/**
 * Callback for processing empty response bodies.
 * @param res The HTTP response object
 */
export async function processEmptyResponse(res: Response): Promise<void> {
  await res.text();
}

/**
 * Utility wrapper for the native `fetch` function.
 * @param url The HTTP resource URL
 * @param options The native `fetch` function options
 * @param callback The HTTP response body processing callback
 * @returns The processed HTTP response body
 */
export function publicFetch<TBody>(
  url: string,
  options: UseApiOptions = {},
  callback: (res: Response) => Promise<TBody> = processJsonResponse,
) {
  return async () => {
    const headers = {
      ...(!["POST", "PUT", "PATCH"].includes(options.method ?? "")
        ? {}
        : { "Content-Type": "application/json" }),
      ...options.headers,
    };
    if (headers["Content-Type"] === "multipart/form-data") {
      delete headers["Content-Type"];
    }

    const res = await fetch(url, {
      ...options,
      ...(!options.body
        ? {}
        : {
            body:
              typeof options.body === "string" ||
              options.body instanceof FormData
                ? options.body
                : JSON.stringify(options.body),
          }),
      headers: headers,
    });

    if (!res.ok) {
      throw new ApiError(res.status, await res.text());
    }

    return (await callback(res)) as TBody;
  };
}

/**
 * Utility wrapper for the native `fetch` function.
 * Options can be specified asynchronously.
 * @param url The HTTP resource URL
 * @param options The native `fetch` function options
 * @param callback The HTTP response body processing callback
 * @returns The processed HTTP response body
 */
export function publicFetchAsync<TBody>(
  url: string,
  optionsAsync: (...args: any[]) => Promise<UseApiOptions> = () =>
    Promise.resolve({}),
  callback = processJsonResponse,
) {
  return async (...args: any[]) => {
    const fetchOptions = await optionsAsync(...args);

    return await publicFetch<TBody>(url, fetchOptions, callback)();
  };
}

/**
 * Utility wrapper for the native `fetch` function with application authentication.
 * @param url The HTTP resource URL
 * @param getAccessToken The Auth0 function to get the access token
 * @param options The native `fetch` function options
 * @param callback The HTTP response body processing callback
 * @returns The processed HTTP response body
 */
export function authenticatedFetch<TBody>(
  url: string,
  getAccessToken: Auth0ContextInterface["getAccessTokenSilently"],
  options: UseApiOptions = {},
  callback = processJsonResponse,
) {
  return async () => {
    const { audience, scope, ...fetchOptions } = options;
    const accessToken = await getAccessToken({
      authorizationParams: { /* audience, */ scope },
    });

    return await publicFetch<TBody>(
      url,
      {
        ...fetchOptions,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...fetchOptions.headers,
        },
      },
      callback,
    )();
  };
}

/**
 * Utility wrapper for the native `fetch` function with application authentication.
 * Options can be specified asynchronously.
 * @param url The HTTP resource URL
 * @param getAccessToken The Auth0 function to get the access token
 * @param options The native `fetch` function options
 * @param callback The HTTP response body processing callback
 * @returns The processed HTTP response body
 */
export function authenticatedFetchAsync<TBody>(
  url: string,
  getAccessToken: Auth0ContextInterface["getAccessTokenSilently"],
  optionsAsync: (...args: any[]) => Promise<UseApiOptions> = () =>
    Promise.resolve({}),
  callback = processJsonResponse,
) {
  return async (...args: any[]) => {
    const { audience, scope, ...fetchOptions } = await optionsAsync(...args);
    const accessToken = await getAccessToken({
      authorizationParams: { /* audience, */ scope },
    });

    return await publicFetch<TBody>(
      url,
      {
        ...fetchOptions,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...fetchOptions.headers,
        },
      },
      callback,
    )();
  };
}

/**
 * Query hook for authenticated API requests with the application API.
 * @param param0 Query configuration, including key, function, and options
 * @returns The react-query hook response
 */
export function useAuthenticatedQuery<
  TBody,
  TData = unknown,
  TQueryKey extends QueryKey = QueryKey,
>({
  queryKey,
  queryFn,
  urlBuilderFn = (key) => `/${key.join("/")}`,
  queryOptions,
  fetchOptions = {},
  responseCallback = processJsonResponse,
}: {
  queryKey: TQueryKey;
  queryFn: (value: SerializedObject<TBody>) => Promise<TData>;
  urlBuilderFn?: (key: TQueryKey) => string;
  queryOptions?: Omit<UseQueryOptions<TData>, "queryKey" | "queryFn">;
  fetchOptions?: UseApiOptions;
  responseCallback?: (res: Response) => Promise<TData>;
}) {
  const { getAccessTokenSilently } = useAuth0();

  // The caller owns queryKey; queryFn also closes over getAccessTokenSilently,
  // fetchOptions and responseCallback, which the rule wants added to it. They
  // cannot be: fetchOptions defaults to a fresh {} per call and
  // getAccessTokenSilently is not referentially stable, so keying on them would
  // miss the cache on every render. As of @tanstack/eslint-plugin-query 5.101.4
  // the rule reports on the useQuery call rather than the queryKey property, so
  // this directive sits here rather than one line above queryKey.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return useQuery({
    ...queryOptions,
    queryKey,
    queryFn: async () => {
      const url = urlBuilderFn(queryKey);
      const data = await authenticatedFetch<SerializedObject<TBody>>(
        `${process.env.REACT_APP_API_ROOT}${url}`,
        getAccessTokenSilently,
        fetchOptions,
        responseCallback,
      )();

      return queryFn(data);
    },
  });
}

/**
 * @deprecated Use `useAuthenticatedQuery` instead
 */
export const useAuthenticatedFetch = <TBody>(
  url: string,
  fetchOptions: UseApiOptions = {},
  queryOptions?: Omit<UseQueryOptions<TBody>, "queryKey" | "queryFn">,
  queryKey: QueryKey = [url],
  responseCallback = processJsonResponse,
) => {
  const { getAccessTokenSilently } = useAuth0();
  const fullUrl = `${process.env.REACT_APP_API_ROOT}${url}`;

  return useQuery({
    queryKey,
    queryFn: authenticatedFetch<TBody>(
      fullUrl,
      getAccessTokenSilently,
      fetchOptions,
      responseCallback,
    ),
    ...queryOptions,
  });
};

/**
 * @deprecated Use `useAuthenticatedQuery` instead
 */
export const useAuthenticatedFileFetch = <TBody>(
  url: string,
  fetchOptions: UseApiOptions = {},
  queryOptions?: Omit<UseQueryOptions<TBody>, "queryKey" | "queryFn">,
  queryKey: QueryKey = [url],
) =>
  useAuthenticatedFetch(
    url,
    fetchOptions,
    queryOptions,
    queryKey,
    processFileResponse,
  );

/**
 * @deprecated Use `useAuthenticatedMutationNew` instead
 */
export const useAuthenticatedMutation = <TBody>(
  url: string,
  fetchOptions: UseApiOptions = {},
  responseCallback = processJsonResponse,
) => {
  const { getAccessTokenSilently } = useAuth0();
  const fullUrl = `${process.env.REACT_APP_API_ROOT}${url}`;

  return authenticatedFetch<TBody>(
    fullUrl,
    getAccessTokenSilently,
    fetchOptions,
    responseCallback,
  );
};

/**
 * Create a mutation hook that includes authenticated fetching and data
 * deserialization.
 * @param param0.mutationKey The mutation query key
 * @param param0.mutationFn The callback function on the returned data
 * @returns The mutation hook
 */
export function useAuthenticatedMutationNew<
  TData = unknown,
  TReturn = unknown,
  TValue = unknown,
  TMutationKey extends MutationKey = MutationKey,
>({
  mutationKey,
  mutationFn,
  urlBuilderFn = (key) => `/${key.join("/")}`,
  mutationOptions,
  responseCallback = processJsonResponse,
}: {
  mutationKey: [string, ...TMutationKey];
  mutationFn: (value: SerializedObject<TData>) => TReturn;
  urlBuilderFn?: (key: TMutationKey) => string;
  mutationOptions?: Omit<
    UseMutationOptions<TReturn, Error, TValue>,
    "mutationKey" | "mutationFn"
  >;
  responseCallback?: (res: Response) => Promise<TData>;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const method = mutationKey[0] ?? "POST";

  return useMutation({
    ...mutationOptions,
    mutationKey,
    mutationFn: async (value: TValue) => {
      const url = urlBuilderFn(mutationKey.slice(1) as [...TMutationKey]);
      const data = await authenticatedFetch<SerializedObject<TData>>(
        `${process.env.REACT_APP_API_ROOT}${url}`,
        getAccessTokenSilently,
        { method, body: value },
        responseCallback,
      )();

      return mutationFn(data);
    },
  });
}

/**
 * @deprecated Use `useAuthenticatedMutationNew` instead
 */
export const useAuthenticatedMutationAsync = <TBody>(
  url: string,
  fetchOptionsAsync: (...args: any[]) => Promise<UseApiOptions> = () =>
    Promise.resolve({}),
  responseCallback = processJsonResponse,
) => {
  const { getAccessTokenSilently } = useAuth0();
  const fullUrl = `${process.env.REACT_APP_API_ROOT}${url}`;

  return authenticatedFetchAsync<TBody>(
    fullUrl,
    getAccessTokenSilently,
    fetchOptionsAsync,
    responseCallback,
  );
};

/**
 * @deprecated Use `useAuthenticatedMutationNew` instead
 */
export const usePublicdMutationAsync = <TBody>(
  url: string,
  fetchOptionsAsync: (...args: any[]) => Promise<UseApiOptions> = () =>
    Promise.resolve({}),
  responseCallback = processJsonResponse,
) => {
  const fullUrl = `${process.env.REACT_APP_API_ROOT}${url}`;

  return publicFetchAsync<TBody>(fullUrl, fetchOptionsAsync, responseCallback);
};

/**
 * Deserialize a date value from an HTTP response body or other source.
 * @param dateValue The original date value to deserialize
 * @returns The deserialized date object, or the original value if empty
 */
export function deserializeDate<
  TValue extends Date | string | undefined | null,
>(dateValue: TValue) {
  if (typeof dateValue === "undefined" || dateValue === null) {
    return dateValue as Extract<TValue, undefined | null>;
  }

  const date = new Date(dateValue);

  if (date.toString() === "Invalid date") {
    throw new Error(`Failed to parse date: ${dateValue}`);
  }

  return date;
}
